import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Factory, RotateCcw, ShieldAlert, Truck, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import LegalDisclaimer, { DISCLAIMER_TEXT } from "@/components/agreements/LegalDisclaimer";
import CopyButton from "@/components/agreements/CopyButton";
import { RED_FLAG_CHECKLISTS, RED_FLAG_BY_ID } from "@/data/agreements/redFlagChecklists";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { ACCENT_CLASSES } from "@/data/agreements/categories";

const ICONS = { Factory, Truck, Warehouse };

/**
 * RedFlagChecklist — route: /agreements/red-flags/:listId
 *
 * A review tool, not a contract. The founder holds the draft in one hand and walks the list:
 * the flag on the left, what goes wrong on the right, a tick box for the pass they are making.
 *
 * Ticks are component state on purpose — this is one review session, not a saved record.
 * The Master Checklist is where a signed/outstanding status belongs.
 */
export default function RedFlagChecklist() {
  const { listId } = useParams();
  const list = RED_FLAG_BY_ID[listId] || null;

  const [checked, setChecked] = useState({});

  // Moving between checklists starts a fresh pass.
  useEffect(() => {
    setChecked({});
  }, [listId]);

  const toggle = useCallback((key) => {
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const sections = list?.sections || [];

  const total = useMemo(
    () => sections.reduce((n, section) => n + (section.items || []).length, 0),
    [sections],
  );

  const reviewed = useMemo(() => {
    let n = 0;
    sections.forEach((section, si) =>
      (section.items || []).forEach((_, ii) => {
        if (checked[`${si}-${ii}`]) n += 1;
      }),
    );
    return n;
  }, [sections, checked]);

  if (!list) {
    return (
      <AgreementsShell
        crumbs={[{ label: "Red flags" }, { label: "Not found" }]}
        title="We do not have that checklist"
        subtitle={`Nothing matches the id "${listId}". These are the ${RED_FLAG_CHECKLISTS.length} red-flag checklists in the library.`}
      >
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {RED_FLAG_CHECKLISTS.map((c) => (
            <ChecklistTile key={c.id} list={c} />
          ))}
        </div>
        <Button asChild variant="outline">
          <Link to="/agreements/library">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to the library
          </Link>
        </Button>
      </AgreementsShell>
    );
  }

  const accent = ACCENT_CLASSES[list.accent] || ACCENT_CLASSES.red;
  const Icon = ICONS[list.icon] || ShieldAlert;
  const doc = DOC_BY_ID[list.docId] || null;
  const percent = total ? Math.round((reviewed / total) * 100) : 0;

  return (
    <AgreementsShell
      crumbs={[
        ...(doc ? [{ label: doc.name, to: `/agreements/library/${doc.id}` }] : []),
        { label: "Red flags" },
      ]}
      title={
        <span className="flex items-start gap-3">
          <span
            className={cn(
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1",
              accent.bg,
              accent.text,
              accent.ring,
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0">{list.title}</span>
        </span>
      }
      subtitle={list.intro}
      actions={
        <CopyButton
          text={checklistToText(list, checked)}
          label="Copy checklist"
          variant="outline"
        />
      }
    >
      {/* Progress */}
      <div className="mb-6 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium tabular-nums">
            {reviewed} of {total} reviewed
          </p>
          <div className="flex items-center gap-3">
            <span className="text-xs tabular-nums text-muted-foreground">{percent}%</span>
            {reviewed > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setChecked({})}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-indigo-600 transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Ticks are for this review pass only — nothing is saved. Copy the list if you want a record.
        </p>
      </div>

      {/* Sections */}
      {sections.map((section, si) => (
        <section key={section.title || si} className="mb-8">
          <h2 className="mb-2 flex flex-wrap items-baseline gap-2 text-lg font-bold tracking-tight">
            {section.title}
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {(section.items || []).length} to check
            </span>
          </h2>

          {/* Column headers only make sense once the two columns exist. */}
          <div className="hidden gap-6 border-b px-4 pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-2 sm:pl-11">
            <span>The flag</span>
            <span>What goes wrong</span>
          </div>

          <ul className="divide-y rounded-lg border bg-card">
            {(section.items || []).map((item, ii) => {
              const key = `${si}-${ii}`;
              const isChecked = Boolean(checked[key]);
              return (
                <li key={key}>
                  <label
                    className={cn(
                      "flex cursor-pointer gap-3 p-4 transition-colors hover:bg-muted/40",
                      isChecked && "bg-muted/30",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(key)}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-indigo-600"
                    />
                    <div className="grid min-w-0 flex-1 gap-1.5 sm:grid-cols-2 sm:gap-6">
                      <p
                        className={cn(
                          "text-sm font-medium leading-relaxed",
                          isChecked && "text-muted-foreground line-through",
                        )}
                      >
                        {item.flag}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        <span className="font-semibold text-foreground sm:hidden">
                          What goes wrong:{" "}
                        </span>
                        {item.consequence}
                      </p>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {/* Where to go next */}
      <div className="mb-6 flex flex-wrap gap-2">
        {doc && (
          <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
            <Link to={`/agreements/library/${doc.id}`}>
              Read the full {doc.name} entry <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
        <CopyButton text={checklistToText(list, checked)} label="Copy checklist" variant="outline" />
        <Button asChild variant="outline">
          <Link to="/agreements/library">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to the library
          </Link>
        </Button>
      </div>

      {/* The other checklists */}
      {RED_FLAG_CHECKLISTS.length > 1 && (
        <section className="mb-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Other red-flag checklists
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {RED_FLAG_CHECKLISTS.filter((c) => c.id !== list.id).map((c) => (
              <ChecklistTile key={c.id} list={c} />
            ))}
          </div>
        </section>
      )}

      <LegalDisclaimer variant="library" />
    </AgreementsShell>
  );
}

function ChecklistTile({ list }) {
  const accent = ACCENT_CLASSES[list.accent] || ACCENT_CLASSES.red;
  const Icon = ICONS[list.icon] || ShieldAlert;
  const count = (list.sections || []).reduce((n, s) => n + (s.items || []).length, 0);
  return (
    <Link
      to={`/agreements/red-flags/${list.id}`}
      className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card className="h-full transition-transform group-hover:-translate-y-0.5 group-hover:border-indigo-300">
        <CardContent className="flex h-full items-start gap-3 p-4">
          <span
            className={cn(
              "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1",
              accent.bg,
              accent.text,
              accent.ring,
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="font-semibold leading-tight">{list.title}</h3>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{list.intro}</p>
            <span className="mt-2 inline-block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {count} things to check
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

/** The whole checklist as plain text, with the current review pass ticked in. */
function checklistToText(list, checked = {}) {
  const lines = [list.title, ""];
  if (list.intro) lines.push(list.intro, "");
  (list.sections || []).forEach((section, si) => {
    lines.push(String(section.title || "").toUpperCase());
    (section.items || []).forEach((item, ii) => {
      lines.push(`[${checked[`${si}-${ii}`] ? "x" : " "}] ${item.flag}`);
      lines.push(`     What goes wrong: ${item.consequence}`);
    });
    lines.push("");
  });
  lines.push("---");
  lines.push(DISCLAIMER_TEXT);
  return lines.join("\n");
}
